import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useProjects } from '@/hooks/useProjects';
import { usePermissions } from '@/hooks/usePermissions';
import { Button, Card, Input } from '@realvisor/ui';
import { api } from '@/lib/api';

type Tab = 'profile' | 'integrations' | 'notifications';

interface EcomailList {
  id: number;
  name: string;
  subscribers_count: number;
}

export default function Settings() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { data: projects } = useProjects();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [name, setName] = useState(user?.user_metadata?.['full_name'] || '');
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [ecomailApiKey, setEcomailApiKey] = useState('');
  const [ecomailListId, setEcomailListId] = useState('');
  const [ecomailEnabled, setEcomailEnabled] = useState(false);
  const [ecomailLists, setEcomailLists] = useState<EcomailList[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [savingEcomail, setSavingEcomail] = useState(false);

  // SIP settings
  const [sipWsUrl, setSipWsUrl] = useState('');
  const [sipDomain, setSipDomain] = useState('');
  const [sipUsername, setSipUsername] = useState('');
  const [sipPassword, setSipPassword] = useState('');
  const [sipCallerId, setSipCallerId] = useState('');
  const [sipEnabled, setSipEnabled] = useState(false);
  const [savingSip, setSavingSip] = useState(false);

  useEffect(() => {
    if (activeTab === 'integrations' && projects && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0]?.id || '');
    }
  }, [activeTab, projects, selectedProjectId]);

  useEffect(() => {
    if (activeTab === 'integrations' && selectedProjectId) {
      loadEcomailSettings();
      loadSipSettings();
    }
  }, [activeTab, selectedProjectId]);

  const loadEcomailSettings = async () => {
    if (!selectedProjectId) return;
    
    try {
      const data = await api.get<any>(`/ecomail/settings?projectId=${selectedProjectId}`);
      setEcomailApiKey(data.ecomailApiKey || '');
      setEcomailListId(data.ecomailListId || '');
      setEcomailEnabled(data.ecomailEnabled || false);
      setEcomailLists([]);
    } catch (error) {
      console.error('Failed to load Ecomail settings', error);
    }
  };

  const fetchEcomailLists = async () => {
    if (!ecomailApiKey) {
      toast.error('Zadejte API klíč');
      return;
    }

    setLoadingLists(true);
    try {
      const data = await api.get<any>(`/ecomail/lists?apiKey=${encodeURIComponent(ecomailApiKey)}`);
      
      if (data.error) {
        toast.error('Nepodařilo se načíst seznamy: ' + data.error);
        setEcomailLists([]);
      } else {
        setEcomailLists(data.lists || []);
        toast.success(`Načteno ${data.lists?.length || 0} seznamů`);
      }
    } catch (error) {
      toast.error('Chyba při načítání seznamů');
      setEcomailLists([]);
    } finally {
      setLoadingLists(false);
    }
  };

  const saveEcomailSettings = async () => {
    if (!selectedProjectId) {
      toast.error('Vyberte projekt');
      return;
    }

    setSavingEcomail(true);
    try {
      await api.post(`/ecomail/settings?projectId=${selectedProjectId}`, {
        ecomailApiKey,
        ecomailListId,
        ecomailEnabled,
      });
      toast.success('Ecomail nastavení uloženo');
    } catch (error) {
      toast.error('Nepodařilo se uložit nastavení');
    } finally {
      setSavingEcomail(false);
    }
  };

  const loadSipSettings = async () => {
    if (!selectedProjectId) return;
    try {
      const data = await api.get<any>(`/sip/settings?projectId=${selectedProjectId}`);
      setSipWsUrl(data.wsUrl || '');
      setSipDomain(data.domain || '');
      setSipUsername(data.username || '');
      setSipPassword(data.password || '');
      setSipCallerId(data.callerId || '');
      setSipEnabled(data.enabled || false);
    } catch (error) {
      console.error('Failed to load SIP settings', error);
    }
  };

  const saveSipSettings = async () => {
    if (!selectedProjectId) {
      toast.error('Vyberte projekt');
      return;
    }

    setSavingSip(true);
    try {
      await api.post(`/sip/settings?projectId=${selectedProjectId}`, {
        wsUrl: sipWsUrl,
        domain: sipDomain,
        username: sipUsername,
        password: sipPassword,
        callerId: sipCallerId,
        enabled: sipEnabled,
      });
      toast.success('SIP nastavení uloženo');
    } catch (error) {
      toast.error('Nepodařilo se uložit SIP nastavení');
    } finally {
      setSavingSip(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      toast.success('Profil byl aktualizován');
    } catch {
      toast.error('Nepodařilo se aktualizovat profil');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'profile' as Tab, label: 'Profil' },
    { id: 'integrations' as Tab, label: 'Integrace' },
    { id: 'notifications' as Tab, label: 'Notifikace' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nastavení</h1>
        <p className="mt-1 text-sm text-gray-500">Správa vašeho účtu a preferencí</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'profile' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card padding="lg">
              <h2 className="text-lg font-semibold text-gray-900">Profil</h2>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <Input
                  label="Jméno a příjmení"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Input
                  label="E-mail"
                  value={user?.email || ''}
                  disabled
                  hint="E-mail nelze změnit"
                />
                <div className="pt-4">
                  <Button type="submit" isLoading={isLoading}>
                    Uložit změny
                  </Button>
                </div>
              </form>
            </Card>

            <Card padding="lg">
              <h2 className="text-lg font-semibold text-gray-900">Změna hesla</h2>
              <form
                className="mt-6 space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (newPassword !== confirmPassword) {
                    toast.error('Hesla se neshodují');
                    return;
                  }
                  if (newPassword.length < 6) {
                    toast.error('Heslo musí mít alespoň 6 znaků');
                    return;
                  }
                  setChangingPassword(true);
                  const { error } = await supabase.auth.updateUser({ password: newPassword });
                  setChangingPassword(false);
                  if (error) {
                    toast.error(`Chyba: ${error.message}`);
                  } else {
                    toast.success('Heslo bylo změněno');
                    setNewPassword('');
                    setConfirmPassword('');
                  }
                }}
              >
                <Input
                  label="Nové heslo"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Input
                  label="Potvrzení hesla"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <div className="pt-4">
                  <Button type="submit" variant="secondary" disabled={changingPassword}>
                    {changingPassword ? 'Ukládám...' : 'Změnit heslo'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          <div className="space-y-6">
            {(can('users:manage') || can('cc_users:manage')) && (
              <Card padding="lg" className="border-blue-200 bg-blue-50">
                <h2 className="text-lg font-semibold text-blue-900">
                  {can('users:manage') ? 'Správa uživatelů' : 'Správa CC týmu'}
                </h2>
                <p className="mt-2 text-sm text-blue-700">
                  {can('users:manage')
                    ? 'Přidávejte a spravujte uživatele v systému.'
                    : 'Spravujte operátory a supervizory call centra.'}
                </p>
                <Button
                  variant="primary"
                  className="mt-4"
                  onClick={() => window.location.href = '/settings/users'}
                >
                  {can('users:manage') ? 'Spravovat uživatele' : 'Spravovat CC tým'}
                </Button>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="max-w-4xl space-y-6">
          <Card padding="lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Výběr projektu</h2>
            <p className="text-sm text-gray-500 mb-4">
              Každý projekt má vlastní nastavení integrace Ecomail
            </p>
            <div className="grid grid-cols-1 gap-2">
              {projects?.map((project) => (
                <button
                  key={project.id}
                  onClick={() => setSelectedProjectId(project.id)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border text-left transition-colors
                    ${selectedProjectId === project.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: project.color }}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{project.name}</div>
                    {project.description && (
                      <div className="text-xs text-gray-500">{project.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {selectedProjectId && (<>
            <Card padding="lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">Ecomail</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Automatická synchronizace leadů do Ecomail email marketingového systému
                  </p>

                  <div className="mt-6 space-y-4">
                    <div>
                      <label className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={ecomailEnabled}
                        onChange={(e) => setEcomailEnabled(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Zapnout Ecomail synchronizaci</span>
                    </label>
                  </div>

                  <Input
                    label="API klíč"
                    type="password"
                    value={ecomailApiKey}
                    onChange={(e) => setEcomailApiKey(e.target.value)}
                    placeholder="Váš Ecomail API klíč"
                    hint="API klíč najdete v Ecomail → Nastavení → Pro vývojáře"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seznam
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={fetchEcomailLists}
                        isLoading={loadingLists}
                        disabled={!ecomailApiKey}
                      >
                        Načíst seznamy
                      </Button>
                    </div>
                    {ecomailLists.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {ecomailLists.map((list) => (
                          <label
                            key={list.id}
                            className={`
                              flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                              ${ecomailListId === list.id.toString()
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:bg-gray-50'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="ecomailList"
                              value={list.id}
                              checked={ecomailListId === list.id.toString()}
                              onChange={(e) => setEcomailListId(e.target.value)}
                              className="h-4 w-4 text-primary-600"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{list.name}</div>
                              <div className="text-xs text-gray-500">
                                {list.subscribers_count} odběratelů
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <Button
                      onClick={saveEcomailSettings}
                      isLoading={savingEcomail}
                    >
                      Uložit nastavení
                    </Button>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">💡 Jak to funguje</h3>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Každý nový lead se automaticky přidá do vybraného seznamu</li>
                    <li>• Kontakt dostane tagy podle zdroje a typu formuláře</li>
                    <li>• Custom fields obsahují zprávu, info o nemovitosti a další data</li>
                    <li>• V timeline leadu se zobrazí aktivita "Odeslán do Ecomail"</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* SIP / Telefonie */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Telefonie / SIP</h2>
                <p className="mt-1 text-sm text-gray-500">
                  WebRTC softphone — volání přímo z prohlížeče s automatickým logováním a nahráváním hovorů
                </p>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={sipEnabled}
                        onChange={(e) => setSipEnabled(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Zapnout SIP telefon</span>
                    </label>
                  </div>

                  <Input
                    label="WebSocket URL"
                    value={sipWsUrl}
                    onChange={(e) => setSipWsUrl(e.target.value)}
                    placeholder="wss://sip.provider.cz:8089/ws"
                    hint="WebSocket adresa vašeho SIP poskytovatele"
                  />

                  <Input
                    label="SIP doména"
                    value={sipDomain}
                    onChange={(e) => setSipDomain(e.target.value)}
                    placeholder="sip.provider.cz"
                    hint="Doména SIP serveru (často stejná jako hostname v URL)"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Uživatelské jméno"
                      value={sipUsername}
                      onChange={(e) => setSipUsername(e.target.value)}
                      placeholder="123456"
                    />
                    <Input
                      label="Heslo"
                      type="password"
                      value={sipPassword}
                      onChange={(e) => setSipPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>

                  <Input
                    label="Caller ID (číslo linky)"
                    value={sipCallerId}
                    onChange={(e) => setSipCallerId(e.target.value)}
                    placeholder="+420123456789"
                    hint="Číslo které se zobrazí volanému"
                  />

                  <div className="pt-4 border-t">
                    <Button
                      onClick={saveSipSettings}
                      isLoading={savingSip}
                    >
                      Uložit nastavení
                    </Button>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">📞 Jak to funguje</h3>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Po zapnutí se v dashboardu zobrazí softphone tlačítko</li>
                    <li>• Kliknutím na telefon u kontaktu/leadu se rovnou vytočí číslo</li>
                    <li>• Hovory se automaticky nahrávají a logují jako aktivity</li>
                    <li>• Funguje s libovolným SIP poskytovatelem (Odorik, Zadarma, Asterisk…)</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
          </>)}
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="max-w-2xl">
          <Card padding="lg">
            <h2 className="text-lg font-semibold text-gray-900">Notifikace</h2>
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                <span className="text-sm text-gray-700">E-mailové notifikace</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                <span className="text-sm text-gray-700">Nové leady</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                <span className="text-sm text-gray-700">Týdenní souhrn</span>
              </label>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
